import type { CSSProperties } from 'react'

// The fixed, behind-content environment for the landing: void field (CSS),
// a slow-drifting constellation (this SVG), and film grain (CSS).
// Star positions are generated deterministically (mulberry32, same seed as
// Diwata-Site/src/components/Atmosphere.astro) so the field is stable and
// identical across the verse — no Math.random, no render churn.
const SEED = 0x9e3779b1

function makeStars() {
  let s = SEED >>> 0
  const rand = () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  return Array.from({ length: 64 }, () => ({
    x: +(rand() * 100).toFixed(2),
    y: +(rand() * 100).toFixed(2),
    r: +(rand() * 0.9 + 0.25).toFixed(2),
    o: +(rand() * 0.5 + 0.2).toFixed(2),
    twinkle: rand() > 0.7,
    delay: +(rand() * 8).toFixed(2),
    gold: rand() > 0.82,
  }))
}

const STARS = makeStars()

// The web: each star reaches for its two nearest neighbors, long edges are
// dropped, and only the shortest 50 survive — so the sky reads as local
// constellations: the logo's connected-graph motif scattered across the void.
const MAX_LINK_DIST = 18
const MAX_LINKS = 50

function makeLinks() {
  const edges: { a: number; b: number; d: number }[] = []
  const seen = new Set<string>()
  STARS.forEach((star, i) => {
    STARS.map((other, j) => ({ j, d: Math.hypot(other.x - star.x, other.y - star.y) }))
      .filter(({ j, d }) => j !== i && d <= MAX_LINK_DIST)
      .sort((p, q) => p.d - q.d)
      .slice(0, 2)
      .forEach(({ j, d }) => {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`
        if (!seen.has(key)) {
          seen.add(key)
          edges.push({ a: i, b: j, d })
        }
      })
  })
  return edges.sort((p, q) => p.d - q.d).slice(0, MAX_LINKS)
}

const LINKS = makeLinks()

// Two ember nodes — the logo's crimson-center motif carried into the sky.
// Fixed hub indices: #37 (79.2, 24.0) upper-right, #63 (41.6, 47.7) mid-left;
// both have degree ≥ 3 in the web and sit clear of screen edges.
const EMBERS = new Set([37, 63])

export function LandingAtmosphere() {
  return (
    <>
      <div className="dwl-atmos" aria-hidden="true" />
      <div className="dwl-constellation" aria-hidden="true">
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          <g className="dwl-constellation-lines">
            {LINKS.map(({ a, b }) => (
              <line key={`${a}-${b}`} x1={STARS[a].x} y1={STARS[a].y} x2={STARS[b].x} y2={STARS[b].y} />
            ))}
          </g>
          <g>
            {STARS.map((star, i) => (
              <circle
                key={i}
                cx={star.x}
                cy={star.y}
                // small and quiet (B+C critique: no gray blobs colliding with text) —
                // the field is texture behind the staged sky, never a rival to it
                r={(EMBERS.has(i) ? star.r * 1.3 : star.r) * 0.32}
                className={[
                  'dwl-star',
                  star.twinkle && 'dwl-star--twinkle',
                  star.gold && 'dwl-star--gold',
                  EMBERS.has(i) && 'dwl-star--ember',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--o': star.o, '--delay': `${star.delay}s` } as CSSProperties}
              />
            ))}
          </g>
        </svg>
      </div>
      <div className="dwl-grain" aria-hidden="true" />
    </>
  )
}
